import { useState } from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import { usePermission } from '../hooks/usePermission';
import { OfflineIndicator } from './OfflineIndicator';
import {
  HomeIcon,
  BuildingOfficeIcon,
  CubeIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  CalendarIcon,
  ArrowsRightLeftIcon,
  UsersIcon,
  ArrowRightOnRectangleIcon,
  QrCodeIcon,
  Bars3Icon,
  XMarkIcon,
  EnvelopeIcon,
  ArrowUpTrayIcon,
  CogIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

export function Layout() {
  const { signOut, currentUser, userData } = useAuth();
  const { currentOrg } = useOrg();
  const navigate = useNavigate();
  const canManageUsers = usePermission('users.manage');
  const canManageInspections = usePermission('inspections.manage');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const navigationSections = [
    {
      title: 'Operations',
      items: [
        { name: 'Dashboard', href: '/', icon: HomeIcon },
        { name: 'Quick Start', href: '/quick-start', icon: QrCodeIcon },
        { name: 'My Jobs', href: '/my-jobs', icon: ClipboardDocumentListIcon },
        { name: 'Inspections', href: '/inspections', icon: ClipboardDocumentCheckIcon },
        { name: 'Schedules', href: '/schedules', icon: CalendarIcon },
      ],
    },
    {
      title: 'Assets & Locations',
      items: [
        { name: 'FLOCs', href: '/flocs', icon: BuildingOfficeIcon },
        { name: 'Assets', href: '/assets', icon: CubeIcon },
      ],
    },
    {
      title: 'Configuration',
      items: [
        { name: 'Templates', href: '/templates', icon: DocumentTextIcon },
        { name: 'Measurements', href: '/measurement-definitions', icon: ChartBarIcon },
        { name: 'SAP Import', href: '/sap-import', icon: ArrowUpTrayIcon, requiresPermission: 'users.manage' },
        { name: 'Equipment Import', href: '/equipment-import', icon: ArrowUpTrayIcon, requiresPermission: 'users.manage' },
        { name: 'Bulk Operations', href: '/bulk-operations', icon: ArrowsRightLeftIcon, requiresPermission: 'inspections.manage' },
      ],
    },
    {
      title: 'Administration',
      requiresPermission: 'users.manage',
      items: [
        { name: 'Users', href: '/users', icon: UsersIcon, requiresPermission: 'users.manage' },
        { name: 'Invitations', href: '/invitations', icon: EnvelopeIcon, requiresPermission: 'users.manage' },
        { name: 'Settings', href: '/settings', icon: CogIcon, requiresPermission: 'users.manage' },
      ],
    },
  ];

  // Filter sections and items based on permissions
  const navigation = navigationSections
    .filter((section) => {
      // Hide entire section if it requires permission user doesn't have
      if (section.requiresPermission === 'users.manage' && !canManageUsers) return false;
      if (section.requiresPermission === 'inspections.manage' && !canManageInspections) return false;

      // Check if section has any visible items
      const visibleItems = section.items.filter((item) => {
        if (!item.requiresPermission) return true;
        if (item.requiresPermission === 'users.manage') return canManageUsers;
        if (item.requiresPermission === 'inspections.manage') return canManageInspections;
        return false;
      });

      return visibleItems.length > 0;
    })
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        if (!item.requiresPermission) return true;
        if (item.requiresPermission === 'users.manage') return canManageUsers;
        if (item.requiresPermission === 'inspections.manage') return canManageInspections;
        return false;
      }),
    }));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-200 flex items-center px-4 z-30">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          {sidebarOpen ? (
            <XMarkIcon className="w-6 h-6 text-gray-600" />
          ) : (
            <Bars3Icon className="w-6 h-6 text-gray-600" />
          )}
        </button>
        <img
          src="/logo.png"
          alt="EasyDone-Pro"
          className="h-8 w-8 ml-3"
        />
        <h1 className="text-lg font-bold text-primary-600 ml-2">EasyDone-Pro</h1>
        {currentOrg && (
          <p className="text-xs text-gray-500 ml-2 truncate">
            {currentOrg.name}
          </p>
        )}
      </div>

      {/* Backdrop overlay for mobile */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 w-64 bg-white border-r border-gray-200 z-50 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo & Org Name */}
          <div className="h-16 px-6 border-b border-gray-200 flex items-center">
            <img
              src="/logo.png"
              alt="EasyDone-Pro"
              className="h-10 w-10 mr-3"
            />
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-primary-600">EasyDone-Pro</h1>
              {currentOrg && (
                <p className="text-xs text-gray-500 truncate">
                  {currentOrg.name}
                </p>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-6 overflow-y-auto">
            {navigation.map((section, sectionIndex) => (
              <div key={section.title}>
                {/* Section Header */}
                <h3 className="px-4 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {section.title}
                </h3>

                {/* Section Items */}
                <div className="space-y-1">
                  {section.items.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className="flex items-center px-4 py-2.5 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <item.icon className="w-5 h-5 mr-3" />
                      {item.name}
                    </Link>
                  ))}
                </div>

                {/* Section Divider (except for last section) */}
                {sectionIndex < navigation.length - 1 && (
                  <div className="mt-4 border-t border-gray-200" />
                )}
              </div>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <Link
                to="/profile"
                className="flex items-center min-w-0 flex-1 hover:bg-gray-50 rounded-lg p-2 -ml-2 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-medium text-primary-600">
                    {userData?.displayName?.charAt(0).toUpperCase() ||
                     currentUser?.email?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="ml-3 min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {userData?.displayName || currentUser?.email}
                  </p>
                  {userData?.role && (
                    <p className="text-xs text-gray-500 truncate">
                      {userData.role.replace('_', ' ')}
                    </p>
                  )}
                </div>
              </Link>
              <button
                onClick={handleSignOut}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                title="Sign out"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        <main className="p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8">
          <Outlet />
        </main>
      </div>

      {/* Offline indicator */}
      <OfflineIndicator />
    </div>
  );
}
